import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SideSheet from "@/components/SideSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DetailSkeleton } from "@/components/Skeletons";
import { fmtDate } from "@/lib/dates";
import { joinMeta, plural } from "@/lib/utils";
import { ATTENDANCE_TEXT_CLASS } from "@/lib/status";
import { useAuth, canWrite } from "@/context/AuthContext";
import { ExternalLink, Pencil, History } from "lucide-react";

const RECENT = 6;

/**
 * One person's training record, read from the employee list without leaving it.
 *
 * `lookup` names the department and unit — the history route hands back the raw
 * employee row, and the list already holds the masters, so nobody fetches them twice.
 */
export default function EmployeePeek({ id, onClose, onEdit, lookup }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const writable = canWrite(user);
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    setD(null);
    api.get(`/employees/${id}/history`)
      .then((r) => { if (live) setD(r.data); })
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail, "That person could not be opened"));
        onClose();
      });
    return () => { live = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const e = d?.employee;
  const recent = [...(d?.trainings || [])]
    .sort((a, b) => String(b.training_date || "").localeCompare(String(a.training_date || "")))
    .slice(0, RECENT);

  return (
    <SideSheet open={!!id} onOpenChange={(v) => !v && onClose()} testid="employee-peek"
               title={e?.full_name || "Employee"}
               description={e ? joinMeta(e.employee_code, e.designation) : "Loading"}
               footer={e && (
                 <>
                   {writable && onEdit && (
                     <Button variant="outline" onClick={() => onEdit(e)} data-testid="peek-emp-edit">
                       <Pencil className="w-4 h-4 mr-1.5" /> Edit
                     </Button>
                   )}
                   <Button variant="outline" onClick={() => nav(`/trainings?employee_id=${id}`)} data-testid="peek-emp-register">
                     <History className="w-4 h-4 mr-1.5" /> In the register
                   </Button>
                   <Button onClick={() => nav(`/employees/${id}`)} data-testid="peek-emp-history">
                     <ExternalLink className="w-4 h-4 mr-1.5" /> Full history
                   </Button>
                 </>
               )}>
      {!d ? <DetailSkeleton testid="employee-peek-skeleton" /> : (
        <div className="space-y-5">
          <section className="text-sm space-y-1" data-testid="peek-emp-profile">
            <div>{joinMeta(lookup?.deptName?.(e.department_id), lookup?.unitName?.(e.unit_id)) || "—"}</div>
            <div className="text-muted-foreground">{joinMeta(e.email, e.mobile) || "No contact details"}</div>
            {e.date_of_joining && <div className="text-muted-foreground">Joined {fmtDate(e.date_of_joining)}</div>}
            <div>
              {e.has_login
                ? <Badge variant="outline" className="text-emerald-700 dark:text-emerald-400 border-emerald-500/40">Has a login</Badge>
                : <Badge variant="outline" className="text-muted-foreground">No login</Badge>}
            </div>
          </section>

          <section className="grid grid-cols-3 gap-2" data-testid="peek-emp-stats">
            <div className="rounded-md border border-border p-3">
              <div className="overline text-muted-foreground">Hours</div>
              <div className="font-heading font-bold text-xl mt-1">{d.total_hours}</div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="overline text-muted-foreground">Attended</div>
              <div className="font-heading font-bold text-xl mt-1">{d.attended}</div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="overline text-muted-foreground">Attendance</div>
              <div className="font-heading font-bold text-xl mt-1">{d.attendance_pct}%</div>
            </div>
          </section>

          <section>
            <div className="overline text-muted-foreground mb-2">
              {d.trainings.length > RECENT ? `Latest ${RECENT} of ${plural(d.trainings.length, "training")}` : plural(d.trainings.length, "training")}
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">Never nominated to a training.</p>
            ) : (
              <ul className="divide-y divide-border">
                {recent.map((t) => (
                  <li key={t.id} className="py-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <button type="button" className="font-medium text-left hover:text-primary truncate"
                              onClick={() => nav(`/trainings/${t.id}`)}>{t.title}</button>
                      <span className={`shrink-0 text-xs ${ATTENDANCE_TEXT_CLASS[t.attendance_status] || ""}`}>{t.attendance_status}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {joinMeta(fmtDate(t.training_date), t.category, t.duration_hours && `${t.duration_hours} hrs`,
                                t.certificate_number && (t.certificate_revoked_at ? `${t.certificate_number} (revoked)` : t.certificate_number))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </SideSheet>
  );
}
